import { FiltersState } from '../hooks/useFilters';
import { getSourceConfig } from '../sources';
import { normalizeSortByForSource } from '../sources/sourceSort';
import { getSourceFilterAdapter } from '../sources/filterAdapters';
import { getSourceModule } from '../sources/sourceModules';
import { formatSalaryDisplay } from './numberFormat';

export { normalizeSortByForSource };

// Константы для фильтров
export const CITIES = [
  'Москва','Санкт-Петербург','Новосибирск','Екатеринбург','Нижний Новгород','Казань','Челябинск','Омск','Самара','Ростов-на-Дону','Уфа','Красноярск','Воронеж','Пермь'
];

export const SOURCE_OPTIONS = [
  { value: 'hh', label: 'HeadHunter' },
  { value: 'estaff', label: 'E-Staff' },
  { value: 'skillaz', label: 'Skillaz' },
  { value: 'avito', label: 'Авито' },
];

// TODO: Вынести определение PROD-домена в конфигурацию (env-переменная), убрать хардкод домена
export function isProdDomain(): boolean {
  if (typeof window === 'undefined') return false;

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
  if (apiBaseUrl.includes('app.relevanter.ru')) {
    return true;
  }

  const hostname = window.location.hostname;
  if (hostname.includes('app.relevanter.ru')) {
    return true;
  }

  return false;
}

export function getSourceOptions() {
  if (isProdDomain()) {
    // Liganayma — только HH (eStaff и Skillaz не доступны)
    return SOURCE_OPTIONS.filter(option => option.value === 'hh');
  }
  return SOURCE_OPTIONS;
}

export const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
  { value: 100, label: '100' }
];

export const SORT_OPTIONS = [
  { value: 'relevance', label: 'По релевантности' },
  { value: 'date_updated', label: 'По дате обновления' },
  { value: 'salary_desc', label: 'По убыванию зарплат' },
  { value: 'salary_asc', label: 'По возрастанию зарплаты' }
];

export const EXCLUDE_UPDATED_OPTIONS = [
  { value: '', label: 'Показать всех' },
  { value: '1_week', label: '1 неделю' },
  { value: '2_weeks', label: '2 недели' },
  { value: '1_month', label: '1 месяц' },
  { value: '2_months', label: '2 месяца' }
];

export const EMPLOYMENT_TYPE_OPTIONS = [
  { value: '', label: 'Любой' },
  { value: 'full', label: 'Полная занятость' },
  { value: 'part', label: 'Частичная занятость' },
  { value: 'project', label: 'Проектная работа' },
  { value: 'volunteer', label: 'Волонтерство' },
  { value: 'probation', label: 'Стажировка' }
];

export const EDUCATION_OPTIONS = [
  { value: 'any', label: 'Не важно' },
  { value: 'secondary', label: 'Среднее' },
  { value: 'special_secondary', label: 'Среднее специальное' },
  { value: 'unfinished_higher', label: 'Неоконченное высшее' },
  { value: 'higher', label: 'Высшее' },
  { value: 'bachelor', label: 'Бакалавр' },
  { value: 'master', label: 'Магистр' },
  { value: 'candidate', label: 'Кандидат наук' },
  { value: 'doctor', label: 'Доктор наук' }
];

const ALLOWED_EDUCATION_IDS = new Set([
  'secondary',
  'special_secondary',
  'unfinished_higher',
  'higher',
  'bachelor',
  'master',
  'candidate',
  'doctor'
]);

export function normalizeEducation(value: string | undefined | null): string {
  if (!value || value === 'any') return '';
  const v = String(value).toLowerCase();
  return ALLOWED_EDUCATION_IDS.has(v) ? v : '';
}

export function normalizeEducationList(value: any): string[] {
  const rawList = Array.isArray(value) ? value : (value ? [value] : []);
  const normalized = rawList
    .map((v) => normalizeEducation(v == null ? '' : String(v)))
    .filter(Boolean);
  return Array.from(new Set(normalized));
}

export const GENDER_OPTIONS = [
  { value: '', label: 'Любой' },
  { value: 'M', label: 'М' },
  { value: 'F', label: 'Ж' }
];

export const COMPANY_FILTER_MODE_OPTIONS = [
  { value: 'current', label: 'Текущая компания' },
  { value: 'all_time', label: 'За все время работы' }
];

export const JOB_SEARCH_STATUS_OPTIONS = [
  { value: 'active_search', label: 'Активно ищет работу' },
  { value: 'looking_for_offers', label: 'Рассматривает предложения' },
  { value: 'has_job_offer', label: 'Предложили работу, решает' },
  { value: 'accepted_job_offer', label: 'Вышел на новое место' },
  { value: 'not_looking_for_job', label: 'Не ищет работу' }
];

export const SCHEDULE_OPTIONS = [
  { value: 'fullDay', label: 'Полный день (офис)' },
  { value: 'remote', label: 'Удаленная работа' }
];

// Опции для E-Staff
export const ESTAFF_EXPERIENCE_OPTIONS = [
  { value: '', label: 'Любой' },
  { value: 'more_1_year', label: 'Более 1 года' },
  { value: 'more_3_years', label: 'Более 3 лет' },
  { value: 'more_5_years', label: 'Более 5 лет' },
  { value: 'more_10_years', label: 'Более 10 лет' }
];

export const ESTAFF_POSITION_EXPERIENCE_OPTIONS = [
  { value: '', label: 'Любой' },
  { value: 'more_1_year', label: 'Более 1 года' },
  { value: 'more_3_years', label: 'Более 3 лет' },
  { value: 'more_5_years', label: 'Более 5 лет' },
  { value: 'more_10_years', label: 'Более 10 лет' }
];

export const ESTAFF_EXPERIENCE_PERIOD_OPTIONS = [
  { value: '', label: 'Любой' },
  { value: 'current', label: 'Текущая' },
  { value: 'last_year', label: 'За последний год' },
  { value: 'last_3_years', label: 'За последние 3 года' },
  { value: 'last_5_years', label: 'За последние 5 лет' },
  { value: 'all_time', label: 'За все время' }
];

// Опции для HeadHunter
export const HH_EXPERIENCE_OPTIONS = [
  { value: '', label: 'Любой' },
  { value: 'noExperience', label: 'Нет опыта' },
  { value: 'between1And3', label: 'От 1 года до 3 лет' },
  { value: 'between3And6', label: 'От 3 до 6 лет' },
  { value: 'moreThan6', label: 'Более 6 лет' }
];

export const HH_POSITION_EXPERIENCE_OPTIONS = [
  { value: '', label: 'Любой' },
  { value: 'noExperience', label: 'Нет опыта' },
  { value: 'between1And3', label: 'От 1 года до 3 лет' },
  { value: 'between3And6', label: 'От 3 до 6 лет' },
  { value: 'moreThan6', label: 'Более 6 лет' }
];

export const HH_EXPERIENCE_PERIOD_OPTIONS = [
  { value: '', label: 'Любой' },
  { value: 'all_time', label: 'За всё время опыта' },
  { value: 'last_year', label: 'За последний год' },
  { value: 'last_three_years', label: 'За последние 3 года' },
  { value: 'last_six_years', label: 'За последние 6 лет' }
];

// Утилитарные функции
export function getExperienceOptions(source: string, type: 'total' | 'position' | 'period') {
  if (source === 'hh') {
    switch (type) {
      case 'total':
        return HH_EXPERIENCE_OPTIONS;
      case 'position':
        return HH_POSITION_EXPERIENCE_OPTIONS;
      case 'period':
        return HH_EXPERIENCE_PERIOD_OPTIONS;
    }
  } else {
    switch (type) {
      case 'total':
        return ESTAFF_EXPERIENCE_OPTIONS;
      case 'position':
        return ESTAFF_POSITION_EXPERIENCE_OPTIONS;
      case 'period':
        return ESTAFF_EXPERIENCE_PERIOD_OPTIONS;
    }
  }
}

export function validateFilters(filters: FiltersState): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Валидация зарплаты
  if (filters.salaryFrom && filters.salaryTo) {
    const from = parseInt(filters.salaryFrom);
    const to = parseInt(filters.salaryTo);
    if (from > to) {
      errors.push('Минимальная зарплата не может быть больше максимальной');
    }
  }

  // Валидация возраста
  const hasAgeFrom = typeof filters.ageFrom === 'string' && filters.ageFrom !== '';
  const hasAgeTo = typeof filters.ageTo === 'string' && filters.ageTo !== '';
  const from = hasAgeFrom ? parseInt(filters.ageFrom as string, 10) : undefined;
  const to = hasAgeTo ? parseInt(filters.ageTo as string, 10) : undefined;

  if (hasAgeFrom) {
    if (Number.isNaN(from as number) || (from as number) < 1) {
      errors.push('Минимальный возраст должен быть не меньше 1');
    } else if ((from as number) > 100) {
      errors.push('Возраст должен быть от 1 до 100 лет');
    }
  }

  if (hasAgeTo) {
    if (Number.isNaN(to as number) || (to as number) < 1) {
      errors.push('Максимальный возраст должен быть не меньше 1');
    } else if ((to as number) > 100) {
      errors.push('Возраст должен быть от 1 до 100 лет');
    }
  }

  if (hasAgeFrom && hasAgeTo && (from as number) > (to as number)) {
    errors.push('Минимальный возраст не может быть больше максимального');
  }

  // Валидация навыков
  if (filters.skillsAny.length > 20) {
    errors.push('Максимальное количество навыков для поиска "ИЛИ": 20');
  }
  if (filters.skillsAll.length > 20) {
    errors.push('Максимальное количество навыков для поиска "И": 20');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

export function getActiveFiltersCount(filters: FiltersState): number {
  const source = filters.source || 'hh';
  const sourceConfig = getSourceConfig(source);
  const hasSection = (section: string) => sourceConfig.filterSections.includes(section as any);
  let count = 0;
  
  if (filters.salaryFrom || filters.salaryTo) count++;
  if (hasSection('education') && Array.isArray(filters.education) && filters.education.length > 0) count++;
  if (filters.cities.length > 0 || (filters.cityIds && filters.cityIds.length > 0)) count++;
  if (
    filters.ageFrom
    || filters.ageTo
    || (hasSection('includeNoAge') && !filters.includeNoAge)
  ) count++;
  if (filters.gender) count++;
  if (hasSection('companies') && filters.stopCompanies.length > 0) count++;
  if (hasSection('companies') && filters.donorCompanies.length > 0) count++;
  if (filters.positionName) count++;
  if ((source === 'estaff' || source === 'skillaz') && filters.expectedPositionName) count++;
  if (source === 'estaff' && (filters.updatedAfter || filters.updatedBefore)) count++;
  if (source === 'skillaz' && filters.updatedAfter) count++;
  if (source === 'estaff' && (filters.createdAfter || filters.createdBefore)) count++;
  if (source === 'skillaz' && filters.createdAfter) count++;
  if (hasSection('experiencePeriod') && filters.experiencePeriod) count++;
  if (hasSection('skills') && filters.skillsAny.length > 0) count++;
  if (hasSection('skills') && filters.skillsAll.length > 0) count++;
  if (hasSection('positionExperience') && filters.totalExperience) count++;
  if (hasSection('positionExperience') && filters.positionExperience) count++;
  if (hasSection('jobSearchStatus') && filters.jobSearchStatus.length > 0) count++;

  count += getSourceModule(source)?.filters?.getActiveFiltersCount?.(filters) || 0;

  return count;
}

export function formatFiltersForDisplay(filters: FiltersState): string[] {
  const displayItems: string[] = [];
  
  if (filters.salaryFrom || filters.salaryTo) {
    const from = filters.salaryFrom ? `от ${formatSalaryDisplay(filters.salaryFrom)}` : '';
    const to = filters.salaryTo ? `до ${formatSalaryDisplay(filters.salaryTo)}` : '';
    const currency = filters.salaryCurrency ? ` ${filters.salaryCurrency}` : '';
    displayItems.push(`Зарплата: ${from} ${to}${currency}`.trim());
  }
  
  if (Array.isArray(filters.education) && filters.education.length > 0) {
    const labels = filters.education
      .map((id) => EDUCATION_OPTIONS.find((opt) => opt.value === id)?.label)
      .filter(Boolean);
    if (labels.length > 0) displayItems.push(`Образование: ${labels.join(', ')}`);
  }
  
  if (filters.cities.length > 0) {
    displayItems.push(`Города: ${filters.cities.join(', ')}`);
  }
  
  if (filters.positionName) {
    displayItems.push(`Должность: ${filters.positionName}`);
  }

  if (filters.expectedPositionName) {
    displayItems.push(`Предполагаемая должность: ${filters.expectedPositionName}`);
  }

  if (filters.skillsAny.length > 0) {
    displayItems.push(`Навыки (ИЛИ): ${filters.skillsAny.join(', ')}`);
  }
  
  if (filters.skillsAll.length > 0) {
    displayItems.push(`Навыки (И): ${filters.skillsAll.join(', ')}`);
  }
  
  if (filters.industry) {
    displayItems.push(`Отрасль: ${filters.industry}`);
  }
  
  return displayItems;
}

export function extractLastPathSegment(value: string | undefined | null): string {
  const v = String(value ?? '').trim();
  if (!v) return '';
  const parts = v.split('/').filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : v;
}

export function createApiQuery(filters: FiltersState): Record<string, any> {
  const sourceAdapter = getSourceFilterAdapter(filters.source);
  if (sourceAdapter) {
    return sourceAdapter.toSearchQuery(filters);
  }

  // TASKNEIROKLYUCH-645: backend per_page берём из filters.limit (то же, что
  // «N резюме» управляет клиентской пагинацией). filters.pageSize — это
  // target count верхнего контрола «Количество кандидатов», он используется
  // ТОЛЬКО как target для relevance-поиска (handleSearch перезаписывает
  // pageSize локальным relevancePageSize). В non-relevance режиме target count
  // не должен течь в backend per_page — иначе «Количество кандидатов» меняет
  // количество выводимых на странице людей, чего быть не должно.
  const query: Record<string, any> = {
    source: filters.source,
    sortBy: filters.sortBy,
    page: filters.page,
    pageSize: filters.limit || 20,
  };

  // Всегда добавляем основные параметры (даже пустые)
  query.excludeUpdatedPeriod = filters.excludeUpdatedPeriod || '';
  query.salaryFrom = filters.salaryFrom || '';
  query.salaryTo = filters.salaryTo || '';
  if ((filters.salaryFrom || filters.salaryTo) && filters.salaryCurrency) {
    query.salaryCurrency = filters.salaryCurrency;
  }
  if (filters.incomeSpecified !== undefined && filters.incomeSpecified !== false) {
    query.incomeSpecified = filters.incomeSpecified;
  }
  const education = normalizeEducationList(filters.education);
  if (education.length > 0) query.education = education;
  query.ageFrom = filters.ageFrom || '';
  query.ageTo = filters.ageTo || '';
  query.includeNoAge = filters.includeNoAge;
  query.gender = filters.gender || '';
  if (filters.hasPhoto) query.hasPhoto = true;
  if (Array.isArray((filters as any).citizenship) && (filters as any).citizenship.length > 0) {
    query.citizenship = (filters as any).citizenship;
  }
  query.companyFilterMode = filters.companyFilterMode;
  query.positionName = filters.positionName || '';
  query.expectedPositionName = filters.expectedPositionName || '';
  query.strictSearch = filters.strictSearch ?? false;
  query.experiencePeriod = filters.experiencePeriod || '';
  query.totalExperience = filters.totalExperience || '';
  query.positionExperience = filters.positionExperience || '';
  query.searchMode = 'custom';
  query.onlyRussia = false;
  if (filters.employmentTypes && filters.employmentTypes.length > 0) query.employment = filters.employmentTypes.join(',');
  else if (filters.employmentType) query.employment = filters.employmentType;
  if (filters.excludeJumpers) query.excludeJumpers = true;
  if (filters.hideViewedByCompany && filters.source === 'hh') query.hideViewedByCompany = true;
  if (filters.filterByExpIndustry) query.filterByExpIndustry = true;
  if ((filters as any).scoringEnabled === false) query.scoringEnabled = false;
  // REL-21: фильтр "Только мои кандидаты" только для E-Staff
  if (filters.onlyMyCandidates === true && filters.source === 'estaff') query.onlyMyCandidates = true;

  // Добавляем массивы только если они не пустые
  if (filters.cityIds && filters.cityIds.length > 0) {
    query.cityIds = filters.cityIds;
  }
  if (filters.cities && filters.cities.length > 0) {
    (query as any).cities = filters.cities;
  }
  if (filters.stopCompanies?.length > 0) query.stopCompanies = filters.stopCompanies;
  if (filters.donorCompanies?.length > 0) query.donorCompanies = filters.donorCompanies;
  if (filters.skillsAny?.length > 0) query.skillsAny = filters.skillsAny;
  if (filters.skillsAll?.length > 0) query.skillsAll = filters.skillsAll;
  if ((filters as any).skillGroups && Array.isArray((filters as any).skillGroups) && (filters as any).skillGroups.length > 0) {
    query.skillGroups = JSON.stringify((filters as any).skillGroups);
  }
  if (filters.jobSearchStatus?.length > 0) query.jobSearchStatus = filters.jobSearchStatus;
  if (filters.metro) query.metro = filters.metro;
  if ((filters as any).strictRegionSearch) query.strictRegionSearch = true;
  if (Array.isArray((filters as any).selectedIndustries) && (filters as any).selectedIndustries.length > 0) {
    query.industry = (filters as any).selectedIndustries;
  } else if (filters.industry) {
    query.industry = [filters.industry];
  }
  // Специализации → professional_roles (для search for resumes)
  if (Array.isArray((filters as any).selectedSpecializations) && (filters as any).selectedSpecializations.length > 0) {
    query.professional_roles = Array.from(
      new Set(
        (filters as any).selectedSpecializations
          .map((v: any) => extractLastPathSegment(String(v)))
          .filter(Boolean)
      )
    );
  } else if ((filters as any).specialization) {
    const id = extractLastPathSegment(String((filters as any).specialization));
    if (id) query.professional_roles = [id];
  }

  // E-Staff специфичные фильтры
  if (filters.currentPositionOnly) query.currentPositionOnly = true;
  if (filters.hasSalary) query.hasSalary = true;
  if (filters.currentCompany) query.currentCompany = filters.currentCompany;
  if (filters.previousCompany) query.previousCompany = filters.previousCompany;
  if (filters.updatedAfter) query.updatedAfter = filters.updatedAfter;
  if (filters.source === 'estaff' && filters.updatedBefore) query.updatedBefore = filters.updatedBefore;
  if (filters.createdAfter) query.createdAfter = filters.createdAfter;
  if (filters.source === 'estaff' && filters.createdBefore) query.createdBefore = filters.createdBefore;

  return query;
}
